import { useSuspenseQuery, noop } from '@tanstack/react-query'
import { useEffect } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { CheckCircle2Icon, ClipboardListIcon, XCircleIcon, UsersIcon } from 'lucide-react'
import { TeamEvaluationPending } from './-components/shared/team-evaluation-pending'
import { Avatar } from './-components/shared/avatar'
import { cn } from '@/lib/utils'
import { resetDragscroll } from '@/lib/dragscroll'
import { TooltipGroup, TooltipGroupTrigger } from '@/components/ui/tooltip'
import { trpc } from '@/main'
import { EVALUATION_CELL, type EvaluationMatrixRow } from 'shared/report-compute'

export const Route = createFileRoute('/_app/teams/$teamId/evaluation')({
  // The N×N matrix is read by THIS leaf only, so it stays here rather than on the $teamId layout — the
  // same reason teams' report query is not lifted (AGENTS.md).
  context: ({ params }) => ({
    evaluationMatrixQueryOptions: trpc.teams.getEvaluationMatrix.queryOptions({ teamId: Number(params.teamId) }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.evaluationMatrixQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: TeamEvaluationPending,
  // Shown immediately: at the router's default pendingMs the NAV STACK cannot push while the loaders run,
  // because the level it would push is the route being rendered — see the full story in jobs.$jobId.report.
})

function RouteComponent() {
  const { t } = useLingui()
  const { teamId } = Route.useParams()
  const context = Route.useRouteContext()
  const { data } = useSuspenseQuery(context.evaluationMatrixQueryOptions)

  useEffect(() => {
    resetDragscroll()
  }, [])

  if (!data.roster.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <div className="bg-muted rounded-full p-4">
          <UsersIcon className="text-muted-foreground size-8" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{t`No team members`}</h3>
          <p className="text-muted-foreground text-sm">{t`Add members to this team to start peer evaluations`}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-7xl flex-col gap-4 px-4 pt-8 pb-4 @sm:px-6 @lg:px-8">
      <div className="flex shrink-0 items-center gap-3">
        <div className="flex size-10 items-center justify-center rounded-xl bg-emerald-500/10">
          <ClipboardListIcon className="size-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h2 className="text-xl font-semibold">{t`Evaluation status`}</h2>
      </div>

      {/* The bordered card is itself the scroll container on both axes: its scrollbar sits at the box's own
          right edge, the sticky <thead> pins to it and the sticky-left evaluator column to its left edge. No
          top/left padding, or the pinned header gaps and cells bleed beside the sticky column. Member columns
          are fixed-width so the grid is even on both axes: a 48px cell in a 56px column, a uniform 8px gap. */}
      <div className="scrollbar dragscroll bg-card min-h-0 w-full flex-1 cursor-grab scrollbar-gutter-stable overflow-auto rounded-xl border pr-3 pb-3">
        <table className="mx-auto table-fixed border-collapse" style={{ width: 180 + 100 + data.roster.length * 56 }}>
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="text-muted-foreground bg-card sticky left-0 z-30 w-[180px] rounded-tl-xl py-3 pr-3 pl-4 text-left text-xs font-medium tracking-wider uppercase">
                {t`Evaluator`}
              </th>
              {/* ONE popup for the header row: sliding along the evaluated members swaps its text rather
                  than closing and reopening a tooltip per column. */}
              <TooltipGroup side="top">
                {data.roster.map((member) => (
                  <th key={member.id} data-column={member.id} className="bg-card w-14 px-0 py-2 text-center">
                    <TooltipGroupTrigger
                      tip={
                        <p>
                          {(() => {
                            const name = member.name
                            return t`Evaluated: ${name}`
                          })()}
                        </p>
                      }
                      render={
                        <div className="flex cursor-help flex-col items-center gap-1.5">
                          <Avatar size="lg" url={member.image} name={member.name} type="member" />
                          <span className="text-muted-foreground max-w-[52px] truncate text-[10px]">
                            {member.name?.split(' ')[0]}
                          </span>
                        </div>
                      }
                    />
                  </th>
                ))}
              </TooltipGroup>
              <th className="text-muted-foreground bg-card w-[100px] rounded-tr-xl p-2 text-center text-xs font-medium tracking-wider uppercase">
                {t`Progress`}
              </th>
            </tr>
          </thead>
          <tbody className="divide-border divide-y">
            {data.matrix.map((row, index) => (
              <EvaluatorRow
                key={data.roster[index]?.id ?? index}
                data={row}
                evaluator={data.roster[index]}
                roster={data.roster}
                teamId={teamId}
                isLast={index === data.matrix.length - 1}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-muted-foreground flex shrink-0 flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="flex size-5 items-center justify-center rounded-md bg-emerald-500/20">
            <CheckCircle2Icon className="size-3.5 text-emerald-600" />
          </div>
          <span>{t`Completed`}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex size-5 items-center justify-center rounded-md bg-red-500/20">
            <XCircleIcon className="size-3.5 text-red-500" />
          </div>
          <span>{t`Pending`}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-muted size-5 rounded-md" />
          <span>{t`Not applicable`}</span>
        </div>
      </div>
    </div>
  )
}

type RosterMember = { id: string; name: string | null; image: string | null }

/** Both axes index the roster, so the evaluator is this row's roster entry and each cell is a column's. */
function EvaluatorRow(props: {
  data: EvaluationMatrixRow
  evaluator: RosterMember | undefined
  roster: RosterMember[]
  teamId: string
  isLast?: boolean
}) {
  const { t } = useLingui()
  const progressPercentage =
    props.data.totalToEvaluate > 0 ? Math.round((props.data.completedCount / props.data.totalToEvaluate) * 100) : 0

  return (
    <tr className="border-none transition-colors">
      <td
        className={cn('bg-card sticky left-0 z-10 py-2 pr-2 pl-4 transition-colors', props.isLast && 'rounded-bl-xl')}
      >
        <Link
          to="/teams/$teamId/report"
          params={{ teamId: props.teamId }}
          search={(prev) => ({
            ...prev,
            first: { type: 'team', id: Number(props.teamId) },
            second: { type: 'member', id: Number(props.evaluator?.id), teamId: Number(props.teamId) },
            selected: 'compare',
          })}
          className="flex items-center gap-3 transition-opacity hover:opacity-80"
        >
          <Avatar size="md" url={props.evaluator?.image} name={props.evaluator?.name} type="member" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{props.evaluator?.name}</p>
            <p className="text-muted-foreground text-xs">
              {props.data.completedCount}/{props.data.totalToEvaluate} {t`evaluated`}
            </p>
          </div>
        </Link>
      </td>

      {/* Driven by the ROSTER, which is what the header columns are drawn from, so a cell cannot land in a
          column the payload did not index it to. `data-evaluation` is that contract as the DOM shows it —
          the one thing only a browser can catch (team-members.e2e.test.ts reads it back per row). */}
      {props.roster.map((member, column) => {
        const cell = props.data.cells[column]
        return (
          <td key={member.id} data-evaluation={cell} className="px-0 py-1 text-center">
            <div className="flex justify-center">
              {cell === EVALUATION_CELL.self ? (
                <div className="bg-muted size-12 rounded-md" />
              ) : cell === EVALUATION_CELL.completed ? (
                <div className="flex size-12 items-center justify-center rounded-md bg-emerald-500/20">
                  <CheckCircle2Icon className="size-6 text-emerald-600" />
                </div>
              ) : (
                <div className="flex size-12 items-center justify-center rounded-md bg-red-500/20">
                  <XCircleIcon className="size-6 text-red-500" />
                </div>
              )}
            </div>
          </td>
        )
      })}

      <td className="p-2">
        <div className="flex items-center gap-2">
          <div className="bg-muted relative h-1.5 flex-1 overflow-hidden rounded-full">
            <div
              className={cn(
                'absolute inset-y-0 left-0 rounded-full transition-[width] duration-500',
                progressPercentage === 100 ? 'bg-emerald-500' : 'bg-primary',
              )}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <span className="text-muted-foreground min-w-[3ch] text-xs tabular-nums">{progressPercentage}%</span>
        </div>
      </td>
    </tr>
  )
}
